import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import { ReactComponent as Upload } from "../../assets/icons/upload.svg";
import { ReactComponent as Uploaded } from "../../assets/icons/success-tick.svg";
import { ReactComponent as Delete } from "../../assets/icons/delete.svg";
import { ReactComponent as Close } from "../../assets/icons/close.svg";
import { ReactComponent as Add } from "../../assets/icons/add.svg";
import Button from "../Button";
import Modal from "../Modal";
import CatalogDetailModal from "../CatalogDetailModal";
import { 
  IconButton,
  Tabs,
  Tab,
  Box,
  Badge,
  Paper,
  Fade,
  Checkbox,
  Switch,
  Typography,
  FormGroup,
  FormControlLabel,
} from '@mui/material'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import { fetchAuthSession } from "aws-amplify/auth";
import { environmentConfig } from "../../config/config";
import { addProduct } from "../../store/slice/propertySlice";
import { getFormattedImageLink } from "../../utils/format";
import { getUser } from "../../services/amplify/helpers";
import {
  useCreatePropertySheetMutation,
  useUpdatePropertySheetMutation,
} from "../../store/api/propertySheetApi";
import {
  getBrandFilterOptions,
  DIRECT_BRANDS_EXCLUDE,
  productMatchesBrandFilter,
} from "../../constants/brandFilter";

// TabPanel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`modal-search-tabpanel-${index}`}
      aria-labelledby={`modal-search-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={500}>
          <Box sx={{ pt: 1 }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

// Helper function to get similar category
const getSimilarCategory = (productType) => {
  if (!productType) return "Other";

  const categories = [
    {
      category: "Furniture",
      subcategories: [
        "Seating",
        "Tables",
        "Storage",
        "Bedroom",
        "Outdoor Furniture",
      ],
    },
    {
      category: "Decor",
      subcategories: ["Wall Decor", "Art", "Accents", "Botanicals"],
    },
    {
      category: "Lighting & Fans",
      subcategories: [
        "Re-chargeable/Plug-in",
        "Hardwired",
        "Outdoor Lighting",
        "Fans",
      ],
    },
    {
      category: "Kitchen & Dining",
      subcategories: ["Kitchen Accessories", "Appliances"],
    },
    {
      category: "Bedding & Bath",
      subcategories: ["Bath", "Bedroom", "Mattresses"],
    },
    {
      category: "Rugs",
      subcategories: ["Area Rugs", "Runner Rugs", "Outdoor Rugs"],
    },
    { category: "Outdoor & Garden", subcategories: [] },
    { category: "Kids & Nursery", subcategories: [] },
    { category: "Organization", subcategories: [] },
    { category: "Fixtures", subcategories: [] },
    { category: "Building Materials", subcategories: [] },
    { category: "Window Treatments", subcategories: [] },
    { category: "Seasonal & Specialty", subcategories: [] },
  ];

  let cleanedProductType = productType
    .replace(/\[.*?\]/g, "")
    .replace(/>/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .trim();

  const productParts = cleanedProductType.split(/\s+/);

  let bestMatch = "Other";
  let highestMatchCount = 0;

  categories.forEach(({ category, subcategories }) => {
    let matchCount = 0;
    const normalizedCategory = category.toLowerCase();

    if (productParts.includes(normalizedCategory)) {
      matchCount += 2;
    }

    subcategories.forEach((subcategory) => {
      const normalizedSubcategory = subcategory.toLowerCase();
      if (productParts.includes(normalizedSubcategory)) {
        matchCount++;
      }
    });

    if (matchCount > highestMatchCount) {
      highestMatchCount = matchCount;
      bestMatch = category;
    }
  });

  return bestMatch;
};

export default function ImageSearchModal({ open, openSet }) {
  const dispatch = useDispatch();
  const { selectedRoom, propertySheet, property, editProperty } = useSelector((state) => state.property);
  const [user, setUser] = useState(null);
  
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [directBrandsOnly, setDirectBrandsOnly] = useState(false);
  const [selectedBrandsFrom, setSelectedBrandsFrom] = useState("");
  const [productDetailModal, setProductDetailModal] = useState({ open: false, data: null });
  const [isFormUpdated, setIsFormUpdated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [createSheet] = useCreatePropertySheetMutation();
  const [updateSheet] = useUpdatePropertySheetMutation();

  const searchResultsBoxRef = useRef(null);

  // Get user on mount
  useEffect(() => {
    getUser().then(setUser);
  }, []);

  useEffect(() => {
    if (searchResults && searchResultsBoxRef.current) {
      searchResultsBoxRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [searchResults]);

  // Only close product detail modal when main modal closes
  useEffect(() => {
    if (!open) {
      setProductDetailModal({ open: false, data: null });
      // Don't reset isFormUpdated - let user decide when to save
    }
  }, [open]);

  const processFile = useCallback((file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select a valid image file");
      return;
    }
    
    setIsUploading(true);
    
    // Simulate upload process
    setTimeout(() => {
      setUploadedImage(file);
      setIsUploading(false);
      setSearchResults(null);
      toast.success("Image uploaded successfully!");
    }, 1000);
  }, []);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast.error("Invalid file type. Please select an image file.");
      return;
    }
    
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0]);
    }
  }, [processFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxSize: 5 * 1024 * 1024,
    multiple: false,
    noClick: false,
  });

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setSearchResults(null);
    setActiveTab(0);
    setSelectedCategory('all');
    setIsFormUpdated(false);
    toast.success("Image removed");
  };

  const handleImageSearch = async () => {
    if (!uploadedImage) {
      toast.error("Please upload an image first");
      return;
    }

    setIsSearching(true);
    
    try {
      const arrayBuffer = await uploadedImage.arrayBuffer();
      
      // Get the current session's JWT token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();
      
      // Use dynamic image search URL from config based on environment
      const imageSearchUrl = environmentConfig.LAMBDA_URL.IMAGE_SEARCH;
      
      const response = await fetch(imageSearchUrl, {
        method: 'POST',
        body: arrayBuffer,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const resObj = data?.data?.[0]; 
      const perCropResults = resObj?.perCropResults || [];
      const imgWise = resObj?.wholeImageResults || [];
      
      // Calculate total product matches from all detected objects
      const totalProductMatches = perCropResults.reduce((total, crop) => {
        return total + (crop.results ? crop.results.length : 0);
      }, 0);
      
      if (data.success) {
        setSearchResults({
          perCropResults,
          imgWise,
          executionTimeMs: data.executionTimeMs
        })
        toast.success(
          `Found ${totalProductMatches + imgWise.length} similar images!`
        )
      } else {
        throw new Error(data.error || 'Search failed');
      }
      
    } catch (error) {
      toast.error(`Search failed: ${error.message}`);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Get unique categories from search results
  const availableCategories = searchResults?.perCropResults?.map(crop => crop.detectedObject) || [];

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const brandFilter = {
    from: selectedBrandsFrom,
    excludeFrom: directBrandsOnly ? DIRECT_BRANDS_EXCLUDE.join(",") : "",
  };

  const handleDirectBrandsToggle = () => {
    setDirectBrandsOnly((prev) => !prev);
  };

  const handleBrandSelect = (value, isChecked) => {
    setSelectedBrandsFrom((prev) => {
      const parts = prev ? prev.split(",").map((s) => s.trim()).filter(Boolean) : [];
      if (isChecked) return parts.includes(value) ? prev : parts.concat(value).join(",");
      return parts.filter((v) => v !== value).join(",");
    });
  };

  // Filter results based on selected category
  const filteredResults = searchResults?.perCropResults?.filter(crop => {
    if (selectedCategory === 'all') return true;
    return crop.detectedObject === selectedCategory;
  }) || [];

  const imgWiseFilteredCount = searchResults?.imgWise?.filter((p) => productMatchesBrandFilter(p, brandFilter)).length ?? 0;

  // Apply brand filter for badge count (product-level tab)
  const filteredCount = selectedCategory === 'all'
    ? searchResults?.perCropResults?.reduce((total, crop) => {
        const matching = (crop.results || []).filter((p) => productMatchesBrandFilter(p, brandFilter));
        return total + matching.length;
      }, 0) || 0
    : filteredResults.reduce((total, crop) => {
        const matching = (crop.results || []).filter((p) => productMatchesBrandFilter(p, brandFilter));
        return total + matching.length;
      }, 0);

  // Handle viewing product details
  const handleViewProductDetails = (product) => {
    const sourceVendor = product.vendor || product.from || '';
    const sourceBrand = product.sourceBrand || product.brand || '';
    const productData = {
      title: product.title || 'Untitled Product',
      price: product.price || 0,
      image_link: product.image_link || '',
      link: product.product_link || product.link || '',
      availability: product.availability || '',
      vendor: sourceVendor || sourceBrand,
      from: sourceVendor,
      category: product.category || getSimilarCategory(product.title),
      quantity: 1,
      productType: product.category || getSimilarCategory(product.title),
      brand: sourceBrand || sourceVendor,
      sourceBrand,
      description: product.description || `Product: ${product.title || 'N/A'}\nBrand: ${sourceBrand || sourceVendor || 'N/A'}\nCategory: ${product.category || 'N/A'}\nAvailability: ${product.availability || 'N/A'}`,
      additionalImageLinks: product.additionalImageLinks || '',
      size: product.size || '',
      material: product.material || '',
      // Add callback to add product to room
      onAddToRoom: (quantity = 1) => {
        handleAddProductToRoom(product, quantity);
      }
    };
    setProductDetailModal({ open: true, data: productData });
  };

  // Save property sheet to database
  const handleSaveChanges = async () => {
    if (!propertySheet || !user) {
      toast.error("Unable to save: Missing required data");
      return;
    }

    // Get property ID from various sources
    const propertyId = property?.id || editProperty?.id || propertySheet?.propertyId;
    if (!propertyId) {
      toast.error("Property ID not found, cannot save");
      return;
    }

    setIsSaving(true);

    try {
      const dataToSend = {
        userId: user.userId,
        propertyId: propertyId,
        buyerTotal: propertySheet.buyerTotal || 0,
        rooms: propertySheet.rooms?.map((room) => ({
          name: room.name,
          items:
            room.items?.map((item) => ({
              id: item.id,
              quantity: item.quantity,
              price: item.price,
              image_link: item.image_link,
              title: item.title,
              product_link: item.product_link,
              vendor: item.vendor,
              availability: item.availability,
              category: item.category,
              room: room.name,
            })) || [],
          itemsCount: room.itemsCount || 0,
          cost: room.cost || 0,
          images: room.images || [],
        })) || [],
      };

      const mutation = Number(propertySheet?.version) > 0 ? updateSheet : createSheet;
      const successMessage = Number(propertySheet?.version) > 0
        ? "Sheet updated successfully"
        : "Sheet created successfully";

      await mutation(JSON.stringify(dataToSend)).unwrap();
      setIsFormUpdated(false);
      toast.success(successMessage);
    } catch (error) {
      console.error("Error saving property sheet:", error);
      toast.error(error?.message || "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle adding product to selected room
  const handleAddProductToRoom = (product, quantity = 1) => {
    if (!selectedRoom) {
      toast.error("Please select a room first");
      return;
    }

    const category = getSimilarCategory(product.title || product.category);
    
    // Handle different field names between perCropResults and imgWise products
    // imgWise products use: id, from, link, brand
    // perCropResults products use: ItemID, vendor, product_link
    // Ensure we always have a unique ID - use ItemID first (perCropResults), then id (imgWise), then generate one
    const productItemID = product.ItemID || product.id || '';
    const productId = productItemID || `img-search-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const sourceVendor = product.vendor || product.from || '';
    const sourceBrand = product.sourceBrand || product.brand || '';
    const vendor = sourceVendor || sourceBrand;
    const productLink = product.product_link || product.link || '';
    
    const formattedImageLink = getFormattedImageLink(
      vendor,
      product.image_link,
      product.additionalImageLinks
    ) || product.image_link || "";

    const productToAdd = {
      id: productId,
      quantity: quantity,
      title: product.title || 'Untitled Product',
      image_link: formattedImageLink || product.image_link || '',
      price: product.price || 0,
      ItemID: productItemID,
      product_link: productLink,
      vendor: vendor,
      from: sourceVendor,
      brand: sourceBrand || vendor,
      sourceBrand,
      availability: product.availability || '',
      category: product.category || category,
      ordered: false,
    };

    dispatch(
      addProduct({
        name: selectedRoom.name,
        product: productToAdd,
      })
    );

    // Mark form as updated (same as catalog products)
    setIsFormUpdated(true);

    toast.success(`Added ${product.title || 'product'} to ${selectedRoom.name}`);
  };

  return (
    <>
    <Modal open={open} openSet={openSet} customWidth="85%">
      <div className="flex flex-col" style={{ maxHeight: '85vh', height: '85vh'}}>
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <h2 className="text-2xl font-bold">Mood Board Search</h2>
          <div className="flex items-center gap-3">
            {isFormUpdated && (
              <Button
                onClick={handleSaveChanges}
                loading={isSaving}
                small
                className="!px-4"
              >
                Save Changes
              </Button>
            )}
            <button
              onClick={() => openSet(false)}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
            >
              <Close className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 min-h-0">
          <div className="p-6">

          {/* Modern Image Upload Section */}
          {!uploadedImage ? (
            <div className="mb-6">
              <div
                {...getRootProps()}
                className={`relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 ${
                  isDragActive
                    ? 'border-primary bg-primary/10 scale-[1.02]'
                    : 'border-gray-300 hover:border-primary/50 hover:bg-primary/5'
                }`}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-4">
                  <div className={`bg-gradient-to-br from-primary/10 to-primary/5 rounded-full p-6 transition-transform duration-300 ${
                    isDragActive ? 'scale-110' : 'group-hover:scale-110'
                  }`}>
                    <Upload className="w-8 h-8 text-primary" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="text-lg font-semibold text-gray-900">
                      {isUploading ? "Uploading..." : isDragActive ? "Drop your mood board here" : "Drop your mood board here or click to browse"}
                    </div>
                    <div className="text-sm text-gray-500">
                      Supports JPG, PNG • Max. 5MB
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-4">
                  {/* Image Preview */}
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                      <img 
                        src={URL.createObjectURL(uploadedImage)} 
                        alt="Uploaded preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 shadow-md">
                      <Uploaded className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm text-gray-900 truncate mb-0.5">
                          {uploadedImage.name}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{Math.round(uploadedImage.size / 1024)}KB</span>
                          <span>•</span>
                          <span>{dayjs().format("DD MMM, YYYY h:mma")}</span>
                        </div>
                      </div>
                      <IconButton 
                        onClick={handleRemoveImage} 
                        size="small"
                        className="hover:bg-red-50 ml-2"
                      >
                        <Delete className="w-4 h-4 text-gray-500" />
                      </IconButton>
                    </div>
                    
                    {/* Compact Search button */}
                    <Button 
                      onClick={handleImageSearch} 
                      disabled={isSearching}
                      small
                      className="!px-4 !py-1.5 text-sm"
                    >
                      {isSearching ? "Searching..." : "Search Similar Images"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results section with Gallery View */}
          {uploadedImage && (
            <div className='bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-6' ref={searchResultsBoxRef}>
              <div className='mb-6'>
                <div className='flex items-center justify-between'>
                  <div>
                    <h3 className='text-lg font-semibold text-gray-900 mb-1'>Search Results</h3>
                    {searchResults && (
                      <div className='text-xs text-gray-500'>
                        Completed in {(searchResults?.executionTimeMs || 0) / 1000}s
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {isSearching ? (
                <div className="text-center text-primary/60 py-12">
                  <div className="animate-pulse">Searching for similar images...</div>
                </div>
              ) : searchResults ? (
                <div className="space-y-6">
                  {/* Tabs Section */}
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      borderBottom: 1,
                      borderColor: 'divider',
                      backgroundColor: 'white'
                    }}
                  >
                    <Tabs 
                      value={activeTab} 
                      onChange={handleTabChange}
                      variant="fullWidth"
                      indicatorColor="primary"
                      textColor="primary"
                      sx={{}}
                    >
                      <Tab 
                        iconPosition="start"
                        label={
                          <Badge 
                            badgeContent={filteredCount} 
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none', ml: 1 } }}
                          >
                            <span>Product Level Matches</span>
                          </Badge>
                        }
                        sx={{ 
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          fontWeight: activeTab === 0 ? 600 : 400
                        }}
                      />
                      <Tab 
                        iconPosition="start"
                        label={
                          <Badge 
                            badgeContent={imgWiseFilteredCount} 
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none', ml: 1 } }}
                          >
                            <span>Image Level Matches</span>
                          </Badge>
                        }
                        sx={{ 
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          fontWeight: activeTab === 1 ? 600 : 400
                        }}
                      />
                    </Tabs>
                  </Paper>

                  {/* Brand filter - in search result area */}
                  {searchResults && (
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Direct Brands Only</Typography>
                        <Switch checked={directBrandsOnly} onChange={handleDirectBrandsToggle} color="primary" size="small" />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Brand</Typography>
                      <FormGroup row sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {getBrandFilterOptions(directBrandsOnly).items.map((item) => (
                          <FormControlLabel
                            key={item.value}
                            control={
                              <Checkbox
                                checked={selectedBrandsFrom.split(',').map((s) => s.trim()).filter(Boolean).includes(item.value)}
                                onChange={(e) => handleBrandSelect(item.value, e.target.checked)}
                                size="small"
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 18 }, color: '#6B7280', '&.Mui-checked': { color: '#28282A' } }}
                              />
                            }
                            label={<span style={{ fontSize: '0.875rem' }}>{item.label}</span>}
                          />
                        ))}
                      </FormGroup>
                    </div>
                  )}

                  {/* Category Filters - Only show for Product Level Matches tab */}
                  {searchResults && availableCategories.length > 0 && activeTab === 0 && (
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={selectedCategory === 'all'}
                            onChange={() => handleCategoryChange('all')}
                            sx={{ 
                              '& .MuiSvgIcon-root': { fontSize: 18 },
                              color: '#6B7280',
                              '&.Mui-checked': { color: '#28282A' }
                            }}
                            size="small"
                          />
                          <span className="text-sm font-medium text-gray-700">All Categories</span>
                        </label>
                        {availableCategories.map((category) => {
                          const crop = searchResults.perCropResults.find(c => c.detectedObject === category);
                          const categoryCount = (crop?.results || []).filter((p) => productMatchesBrandFilter(p, brandFilter)).length;
                          return (
                            <label key={category} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={selectedCategory === category}
                                onChange={() => handleCategoryChange(category)}
                                sx={{ 
                                  '& .MuiSvgIcon-root': { fontSize: 18 },
                                  color: '#6B7280',
                                  '&.Mui-checked': { color: '#28282A' }
                                }}
                                size="small"
                              />
                              <span className="text-sm capitalize text-gray-700">
                                {category} ({categoryCount})
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* No products for current brand filter */}
                  {searchResults && filteredCount === 0 && imgWiseFilteredCount === 0 && (directBrandsOnly || selectedBrandsFrom.trim()) && (
                    <div className="p-4 text-center">
                      <p className="text-gray-800 font-medium">No products match your selected brand filter.</p>
                      <p className="text-gray-700 text-sm mt-1">Try selecting different brands or clear the filter to see all results.</p>
                    </div>
                  )}

                  {/* Tab Panels with Gallery View */}
                  <TabPanel value={activeTab} index={0}>
                    {filteredResults.length > 0 ? (
                      <div className="space-y-6">
                        {filteredResults.map((cropResult, cropIndex) => {
                          const brandFilteredResults = (cropResult.results || []).filter((product) => productMatchesBrandFilter(product, brandFilter));
                          if (brandFilteredResults.length === 0) return null;
                          
                          return (
                            <div key={cropIndex} className="space-y-5 mb-6">
                              {/* Detected Object Header */}
                              <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold capitalize shadow-sm">
                                  {cropResult.detectedObject}
                                </div>
                                <span className="text-sm text-gray-500 font-medium">
                                  {brandFilteredResults.length} match{brandFilteredResults.length !== 1 ? 'es' : ''}
                                </span>
                              </div>
                              
                              {/* iPhone-style Gallery Grid */}
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {brandFilteredResults.map((product, index) => (
                                  <div 
                                    key={`${cropResult.detectedObject}-${product.ItemID || index}`} 
                                    className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                                    onClick={() => handleViewProductDetails(product)}
                                  >
                                    {/* Image Container - Square Aspect Ratio */}
                                    <div className="aspect-square bg-gray-100 relative overflow-hidden">
                                      {product.image_link ? (
                                        <img
                                          src={product.image_link}
                                          alt={product.title || 'Product'}
                                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                      ) : null}
                                      <div className="hidden w-full h-full bg-gray-200 items-center justify-center text-gray-500 text-xs">
                                        No Image
                                      </div>
                                    </div>

                                    {/* Minimal Product Info - Bottom */}
                                    <div className="p-3">
                                      <h3 className="font-medium text-sm line-clamp-2 text-gray-900 mb-2 leading-tight">
                                        {product.title || 'Untitled Product'}
                                      </h3>
                                      <div className="flex items-center justify-between">
                                        <div className="text-base font-bold text-primary">
                                          ${product.price || '0'}
                                        </div>
                                        <IconButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddProductToRoom(product, 1);
                                          }}
                                          className="!p-1.5 hover:bg-primary/10"
                                          size="small"
                                          aria-label="Add Product"
                                        >
                                          <Add className="h-4 w-4 text-gray-700" />
                                        </IconButton>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-12">
                        No similar products found
                      </div>
                    )}
                  </TabPanel>

                  <TabPanel value={activeTab} index={1}>
                    {searchResults.imgWise.filter((p) => productMatchesBrandFilter(p, brandFilter)).length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {searchResults.imgWise
                          .filter((p) => productMatchesBrandFilter(p, brandFilter))
                          .map((product, index) => (
                          <div 
                            key={product.id+index} 
                            className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                            onClick={() => handleViewProductDetails(product)}
                          >
                            {/* Image Container - Square Aspect Ratio */}
                            <div className="aspect-square bg-gray-100 relative overflow-hidden">
                              {product.image_link ? (
                                <img
                                  src={product.image_link}
                                  alt={product.title || 'Product'}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className='hidden w-full h-full bg-gray-200 items-center justify-center text-gray-500 text-xs'>
                                No Image
                              </div>
                            </div>

                            {/* Minimal Product Info - Bottom */}
                            <div className="p-3">
                              <h3 className="font-medium text-sm line-clamp-2 text-gray-900 mb-2 leading-tight">
                                {product.title || 'Untitled Product'}
                              </h3>
                              <div className="flex items-center justify-between">
                                <div className="text-base font-bold text-primary">
                                  ${product.price || '0'}
                                </div>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddProductToRoom(product, 1);
                                  }}
                                  className="!p-1.5 hover:bg-primary/10"
                                  size="small"
                                  aria-label="Add Product"
                                >
                                  <Add className="h-4 w-4 text-gray-700" />
                                </IconButton>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-12">
                        No similar products found
                      </div>
                    )}
                  </TabPanel>

                </div>
              ) : (
                <div className="text-center text-primary/60 py-12">
                  Click "Search Similar Images" to find similar images
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </Modal>
    
    {/* Product Detail Modal - Outside main modal to avoid nesting */}
    <CatalogDetailModal
      open={productDetailModal.open}
      openSet={(isOpen) => setProductDetailModal((prev) => ({ ...prev, open: isOpen }))}
      data={productDetailModal.data}
    />
  </>
  );
}

